import { CalendarIcon, GripIcon, RouteIcon } from "lucide-react";
import type {
  CSSProperties,
  FocusEventHandler,
  KeyboardEventHandler,
  PointerEventHandler,
  ReactElement,
  ReactNode,
  Ref,
} from "react";

import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { cn } from "@/lib/utils";

/*
 * The channel dashboard at rest, as markup. RecordDashboardLens, its saved
 * widgets and the layout grid keep the data, the saved definition, layout
 * saves and the drag and keyboard arranging, and render through these, so
 * fixture widgets compose the same dashboard from positions already set.
 */

/** A widget's place on the grid, in columns and rows from the top left. */
export type DashboardWidgetPlacement = { x: number; y: number; w: number; h: number };

/** Widgets in reading order, top row first, as the grid lists them. */
export function orderDashboardWidgets<
  T extends { id: string; position: DashboardWidgetPlacement },
>(widgets: readonly T[]): T[] {
  return [...widgets].sort(
    (left, right) =>
      left.position.y - right.position.y ||
      left.position.x - right.position.x ||
      left.id.localeCompare(right.id),
  );
}

/** The dashboard's scroll surface, framed by the widgets' own gap, with the saved filters above them. */
export function RecordDashboardLensView({
  gap,
  filters,
  children,
}: {
  /** The saved layout's gap between widgets; the default layout's is 12. */
  gap: number;
  filters?: ReactNode;
  children?: ReactNode;
}) {
  // Frame the panel with the SAME spacing the widgets use between each other, so the
  // outer padding and the inter-widget gap read as one consistent rhythm.
  return (
    <section
      className="mlx-floating-panel-scroll-surface relative min-h-0 flex-1 overflow-auto bg-[var(--channel-canvas)] scrollbar-thin"
      style={{ padding: `${gap}px` }}
    >
      {filters ? <div style={{ marginBottom: `${gap}px` }}>{filters}</div> : null}
      {children}
    </section>
  );
}

/** The widgets' frame: layout saves announced, any progress line, then the grid, dimmed while it refreshes. */
export function DashboardWidgetsView({
  announcement = "",
  progress,
  refreshing = false,
  after,
  children,
}: {
  /** "Saving layout." or "Layout saved." while a layout change saves. */
  announcement?: string;
  /** The loading or refreshing line. */
  progress?: ReactNode;
  refreshing?: boolean;
  /** After the grid: the data trace sheet in the application. */
  after?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <>
      <span className="sr-only" aria-live="polite">
        {announcement}
      </span>
      {progress}
      <div
        data-refreshing={refreshing ? "" : undefined}
        className="transition-opacity duration-150 data-[refreshing]:opacity-60"
      >
        {children}
      </div>
      {after}
    </>
  );
}

/** The dashboard grid: its columns, row height and gap as variables, and a note where arranging needs a wider view. */
export function DashboardLayoutGridView({
  layout,
  arrangeable = false,
  dragging = false,
  announcement = "",
  gridRef,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  children,
}: {
  layout: { columns: number; rowHeight: number; gap: number };
  /** A saved view the viewer can arrange: its widgets carry move and resize handles. */
  arrangeable?: boolean;
  /** A widget is being moved or resized. */
  dragging?: boolean;
  /** How the last move or resize left the widget. */
  announcement?: string;
  gridRef?: Ref<HTMLDivElement>;
  onPointerMove?: PointerEventHandler<HTMLDivElement>;
  onPointerUp?: PointerEventHandler<HTMLDivElement>;
  onPointerCancel?: PointerEventHandler<HTMLDivElement>;
  /** The widget cells, in reading order. */
  children?: ReactNode;
}) {
  return (
    <div className="mlx-dashboard-layout-container">
      {arrangeable ? (
        <p className="mlx-dashboard-layout-mobile-note" role="note">
          Layout editing is available in a wider view.
        </p>
      ) : null}
      <div
        ref={gridRef}
        className={cn(
          "mlx-dashboard-grid",
          arrangeable ? "mlx-dashboard-grid-arrangeable" : "",
          dragging ? "mlx-dashboard-grid-dragging" : "",
        )}
        style={
          {
            "--mlx-dashboard-columns": layout.columns,
            "--mlx-dashboard-row-height": `${layout.rowHeight}px`,
            "--mlx-dashboard-gap": `${layout.gap}px`,
          } as CSSProperties
        }
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        {children}
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {announcement}
        </span>
      </div>
    </div>
  );
}

/** A widget's cell on the grid, placed by its column and row variables. */
export function DashboardGridItemView({
  position,
  metric = false,
  active = false,
  onPointerDown,
  children,
}: {
  position: DashboardWidgetPlacement;
  /** A metric tile, which keeps a taller minimum on a phone. */
  metric?: boolean;
  /** The widget being moved or resized. */
  active?: boolean;
  onPointerDown?: PointerEventHandler<HTMLDivElement>;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "mlx-dashboard-grid-item",
        metric ? "mlx-dashboard-grid-item-metric" : "",
        active ? "mlx-dashboard-grid-item-active" : "",
      )}
      style={
        {
          "--mlx-dashboard-column-start": position.x + 1,
          "--mlx-dashboard-column-span": position.w,
          "--mlx-dashboard-row-start": position.y + 1,
          "--mlx-dashboard-row-span": position.h,
        } as CSSProperties
      }
      onPointerDown={onPointerDown}
    >
      {children}
    </div>
  );
}

/** An arrangeable widget's move and resize handles, and its size while it is being resized. */
export function DashboardWidgetHandlesView({
  title,
  dimension = null,
  onMovePointerDown,
  onMoveKeyDown,
  onResizePointerDown,
  onResizeKeyDown,
  onBlur,
}: {
  title: string;
  /** Columns and rows, shown while a resize is under way. */
  dimension?: { w: number; h: number } | null;
  onMovePointerDown?: PointerEventHandler<HTMLButtonElement>;
  onMoveKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
  onResizePointerDown?: PointerEventHandler<HTMLButtonElement>;
  onResizeKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
  onBlur?: FocusEventHandler<HTMLButtonElement>;
}) {
  return (
    <>
      <button
        type="button"
        className="mlx-dashboard-widget-handle mlx-dashboard-widget-move-handle"
        aria-label={`Move ${title}. Use arrow keys to reposition.`}
        aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown"
        title={`Move ${title}`}
        onPointerDown={onMovePointerDown}
        onKeyDown={onMoveKeyDown}
        onBlur={onBlur}
      >
        <GripIcon className="size-3" />
      </button>
      <button
        type="button"
        className="mlx-dashboard-widget-handle mlx-dashboard-widget-resize-handle"
        aria-label={`Resize ${title}. Use arrow keys to resize.`}
        aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown"
        title={`Resize ${title}`}
        onPointerDown={onResizePointerDown}
        onKeyDown={onResizeKeyDown}
        onBlur={onBlur}
      />
      {dimension ? (
        <span className="mlx-dashboard-widget-dimension" aria-hidden>
          {dimension.w} × {dimension.h}
        </span>
      ) : null}
    </>
  );
}

/** A widget header's trace action, which opens the data behind the widget. */
export function DashboardWidgetTraceButton({
  title,
  onTrace,
}: {
  /** The widget's title. */
  title: string;
  onTrace?: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      aria-label={`Trace data for ${title}`}
      title={`Trace data for ${title}`}
      className="text-tertiary-foreground hover:text-foreground"
      // The header is the drag surface; the trace action keeps its own clicks.
      onPointerDown={onTrace ? (event) => event.stopPropagation() : undefined}
      onClick={
        onTrace
          ? (event) => {
              event.stopPropagation();
              onTrace();
            }
          : undefined
      }
    >
      <RouteIcon className="size-3.5" aria-hidden />
    </Button>
  );
}

/** The saved filters' row above the widgets. */
export function DashboardFilterChipsView({ children }: { children?: ReactNode }) {
  return (
    <div
      data-slot="dashboard-filter-chips"
      className="flex flex-wrap items-center gap-2"
      role="group"
      aria-label="Dashboard filters"
    >
      {children}
    </div>
  );
}

/** A saved select filter: its title and the value it applies, or all of them. */
export function DashboardSelectFilterView({
  title,
  allLabel,
  options,
  selected = null,
  onSelect,
}: {
  title: string;
  /** The choice that applies no filter, such as "All stages". */
  allLabel: string;
  options: readonly string[];
  selected?: string | null;
  onSelect?: (value: string) => void;
}) {
  return (
    <label className="flex min-w-0 items-center gap-1.5 rounded-lg border border-border/60 bg-muted/30 py-0.5 pl-2.5 pr-0.5">
      <span className="shrink-0 text-xs font-medium text-muted-foreground">
        {title}
      </span>
      <NativeSelect
        className="h-7 w-auto max-w-44 border-0 bg-transparent text-xs hover:bg-muted/70"
        {...(onSelect
          ? {
              value: selected ?? "",
              onChange: (event) => onSelect(event.target.value),
            }
          : { defaultValue: selected ?? "" })}
        aria-label={`${title} filter`}
      >
        <option value="">{allLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </NativeSelect>
    </label>
  );
}

/** A saved date filter at rest: its title and the range it applies; `menu` wraps the button in its picker. */
export function DashboardDateFilterView({
  title,
  label,
  menu = (trigger) => trigger,
}: {
  title: string;
  /** The applied range as the button names it, such as "All dates". */
  label: string;
  menu?: (trigger: ReactElement) => ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-center gap-1.5 rounded-lg border border-border/60 bg-muted/30 py-0.5 pl-2.5 pr-0.5">
      <span className="shrink-0 text-xs font-medium text-muted-foreground">
        {title}
      </span>
      {menu(
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 max-w-56 justify-start gap-1.5 px-2 text-xs font-normal hover:bg-muted/70"
          aria-label={`${title} date filter`}
        >
          <CalendarIcon className="size-3.5 shrink-0" />
          <span className="truncate">{label}</span>
        </Button>,
      )}
    </div>
  );
}
